/**
 * #902 — the Today / This Week discovery controls, and the way back.
 *
 * Controlled by contract (#902 / #754 "Component boundary"): every value on
 * screen arrives as a prop and every button only REPORTS what the trader asked
 * for. The board that mounts this owns the reading state, the selection, the
 * feed scope and every mutation, so nothing here can narrow a view, fetch,
 * navigate, select a market or touch a ticket or arming on its own. That is
 * why its only runtime imports are React and the class-name helper — the
 * module-import test in `DiscoveryModeControls.test.tsx` holds it there.
 *
 * Two things it deliberately does NOT do:
 *
 * - Compute a date range. `discoveryRange` (`discovery.ts`) decides which days
 *   Today and This Week mean. The board should pass the `range` that
 *   `selectDiscoveryGroups` returns — the one the rows were actually filtered
 *   with — so the dates named here are the dates listed. This file only names
 *   the range it is handed.
 * - Look like the toolbar's own Today. That chip means "today, on the sport you
 *   are on"; these mean "today, across every sport". The visible group label is
 *   what keeps the two from being two indistinguishable Today buttons.
 */
import { useId } from 'react';
import { cn } from '@/lib/infra/utils';
import type { DiscoveryMode, DiscoveryReading } from './discovery';

export interface DiscoveryModeControlsProps {
  mode: DiscoveryMode;
  /** The inclusive reader-local range the rows were selected over. */
  range: DiscoveryReading['manualRange'];
  /** The reader's IANA time zone that range was resolved in. */
  timeZone: string;
  /** Every eligible group, including the ones not yet on screen. */
  eligibleCount: number;
  /** How many of those are on screen now. */
  visibleCount: number;
  /** What the result set cannot see, composed by the board. */
  coverageSummary: string | null;
  onModeChange: (mode: DiscoveryMode) => void;
  onShowMore: () => void;
  onReturnToManual: () => void;
}

const DISCOVERY_MODES: readonly { mode: DiscoveryMode; label: string }[] = [
  { mode: 'today', label: 'Today' },
  { mode: 'week', label: 'This week' },
];

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric',
});
const DAY_WITH_YEAR = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
});

/**
 * A bare calendar date, named from its own parts.
 *
 * `new Date('2026-09-19')` is UTC midnight, which every zone west of Greenwich
 * displays as Sep 18. Building the instant in UTC and formatting it in UTC keeps
 * the day the one named, whatever zone the browser is in. An impossible date
 * (`2026-02-31`, which `Date.UTC` would roll into March) is refused rather than
 * shown as a different day.
 */
function dayLabel(iso: string, withYear: boolean): string | null {
  const match = ISO_DATE.exec(iso);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }
  return (withYear ? DAY_WITH_YEAR : DAY).format(date);
}

/**
 * `Sat, Sep 19` or `Mon, Sep 14 – Sun, Sep 20`. Weekdays are named because the
 * week definition is still an open product question: a trader can see it runs
 * Monday to Sunday, not football's Tuesday to Monday. A range spanning two
 * years names both, as the toolbar's own slate label does.
 */
function rangeLabel(range: DiscoveryModeControlsProps['range']): string | null {
  if (!range || range.startIso > range.endIso) return null;
  if (range.startIso === range.endIso) return dayLabel(range.startIso, false);
  const withYear = range.startIso.slice(0, 4) !== range.endIso.slice(0, 4);
  const start = dayLabel(range.startIso, withYear);
  const end = dayLabel(range.endIso, withYear);
  return start && end ? `${start} – ${end}` : null;
}

function games(count: number): string {
  return `${count} ${count === 1 ? 'game' : 'games'}`;
}

function isCount(value: number): boolean {
  return Number.isSafeInteger(value) && value >= 0;
}

export function DiscoveryModeControls({
  mode, range, timeZone, eligibleCount, visibleCount, coverageSummary,
  onModeChange, onShowMore, onReturnToManual,
}: DiscoveryModeControlsProps) {
  const labelId = useId();
  const active = mode !== 'manual';
  const dates = rangeLabel(range);
  // A count the board could not produce is left unsaid rather than guessed at.
  const counts = isCount(eligibleCount) && isCount(visibleCount);
  const remaining = counts ? Math.max(0, eligibleCount - visibleCount) : 0;

  return (
    <div data-testid="discovery-mode-controls" className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        {/* Outside the chip box, as the ChipFilter atom places its label:
            inside it, in the unpressed chips' grey, it read as a third button
            — beside the toolbar's own "All sports" chip, one to click. */}
        <span id={labelId} className="text-muted-foreground shrink-0 text-xs font-medium">
          Across all sports
        </span>
        {/* The toolbar's ChipGroup / Chip classes, repeated rather than
            imported: both are private to DashboardToolbar.tsx, and exporting
            them would put a toolbar edit into a component-only change. */}
        <div
          role="group"
          aria-labelledby={labelId}
          className="bg-muted/50 border-border/50 flex items-center rounded-lg border p-1"
        >
          {DISCOVERY_MODES.map((option) => (
            <button
              key={option.mode}
              type="button"
              aria-pressed={mode === option.mode}
              // Already there is not a request; re-sending it would only give
              // the board a reason to reset a page the trader expanded.
              onClick={() => { if (mode !== option.mode) onModeChange(option.mode); }}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                mode === option.mode
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
        {active && (
          <button
            type="button"
            onClick={onReturnToManual}
            className="border-border text-muted-foreground hover:text-foreground rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors"
          >
            Back to my board
          </button>
        )}
      </div>

      {active && (
        <div
          data-testid="discovery-mode-scope"
          role="status"
          className="text-muted-foreground mt-1.5 space-y-0.5 break-words text-xs [overflow-wrap:anywhere]"
        >
          <p>
            <span className="text-foreground">{dates ?? 'Dates unavailable'}</span>
            <span aria-hidden="true"> · </span>
            <span>{timeZone.trim() ? `${timeZone} time` : 'Time zone unavailable'}</span>
            {counts && eligibleCount > 0 && (
              <>
                <span aria-hidden="true"> · </span>
                <span className="text-foreground">
                  {remaining > 0 ? `Showing ${visibleCount} of ${games(eligibleCount)}` : games(eligibleCount)}
                </span>
              </>
            )}
            {remaining > 0 && (
              <>
                <span aria-hidden="true"> · </span>
                <button
                  type="button"
                  onClick={onShowMore}
                  className="text-primary underline-offset-2 hover:underline"
                >
                  Show all {remaining} more
                </button>
              </>
            )}
          </p>
          {counts && eligibleCount === 0 ? (
            <p>
              {mode === 'today'
                ? 'No games today in any sport. Try This week, or go back to your board.'
                : 'No games this week in any sport. Go back to your board to choose another date.'}
            </p>
          ) : (
            // `selectDiscoveryGroups` orders every non-manual reading this way,
            // which is not the board's usual order, so it is said rather than
            // left for the trader to reverse-engineer from the rows.
            <p>Biggest total traded volume first; games with no volume figure come last.</p>
          )}
          {coverageSummary && <p>{coverageSummary}</p>}
        </div>
      )}
    </div>
  );
}
